import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  Type,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatIconButton } from '@angular/material/button';
import { MatFormFieldModule, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyFieldProps,
  FormlyModule,
} from '@ngx-formly/core';
import { dsDtos } from '@vectronic/openApi-customer';
import { filter } from 'rxjs';
import { CustomErrorStateMatcher } from '../../utils/custom-error-state-matcher';
import { findFieldsByKey } from '../../utils/find-fields-by-key';
import { AnimalSpeciesService } from './animal-species.service';

export interface AnimalSpeciesAutocompleteProps extends FormlyFieldProps {
  reloadBtn?: boolean;
  protectiveBeltUnavailableReason?: string;
}

export interface FormlyAnimalSpeciesAutocompleteFieldConfig extends FormlyFieldConfig<AnimalSpeciesAutocompleteProps> {
  type: 'animal-species-autocomplete' | Type<AnimalSpeciesAutocompleteComponent>;
}

@Component({
  selector: 'vectronic-formly-animal-species-autocomplete',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    MatIconButton,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatSuffix,
    FormlyModule,
  ],
  templateUrl: './animal-species-autocomplete.component.html',
  styleUrls: ['./animal-species-autocomplete.component.scss'],
})
export class AnimalSpeciesAutocompleteComponent
  extends FieldType<FieldTypeConfig<AnimalSpeciesAutocompleteProps>>
  implements OnInit
{
  private animalSpeciesService = inject(AnimalSpeciesService);
  private destroyRef = inject(DestroyRef);
  animalSpecies = this.animalSpeciesService.animalSpecies;
  search = signal('');
  filteredOptions = computed(() =>
    this.animalSpecies().filter((species) => this._filter(species, this.search())),
  );
  matcher = new CustomErrorStateMatcher();

  constructor() {
    super();
    effect(() => {
      if (this.animalSpeciesService.allSpecies().length > 0 && this.formState['isAdmin']) {
        this.formControl.addValidators((control: AbstractControl) =>
          control.getRawValue() === 'Other'
            ? {
                OtherAnimalSpecies: {
                  message:
                    "Animal Species is 'Other'. Please create the proper species and select it from the dropdown.",
                },
              }
            : null,
        );
      }
    });
  }

  ngOnInit(): void {
    this.formControl.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this.formControl.dirty),
      )
      .subscribe((speciesId) => {
        this.autoTickProtectiveBelt(speciesId);
      });
  }

  private autoTickProtectiveBelt(speciesId: string): void {
    if (!this.animalSpeciesService.isMandatoryProtectiveBeltSpecies(speciesId)) {
      return;
    }
    findFieldsByKey(this.field, 'belt.protective').forEach((beltProtectiveField) => {
      const control = beltProtectiveField.formControl;
      if (control && !control.value) {
        control.setValue(true);
      }
    });
  }

  private _filter(animalSpecies: dsDtos.AnimalSpecies, searchterm: string): boolean {
    if (!searchterm) {
      return true;
    }
    const value = searchterm.toLowerCase();
    return (
      animalSpecies.english?.toLowerCase().includes(value) ||
      animalSpecies.german?.toLowerCase().includes(value) ||
      animalSpecies.latin?.toLowerCase().includes(value) ||
      animalSpecies.tag?.toLowerCase().includes(value)
    );
  }

  protected protectiveBeltNotice(species: dsDtos.AnimalSpecies): string | null {
    return this.lacksMandatoryProtectiveBelt(species.id)
      ? `Needs a protective belt. ${this.props.protectiveBeltUnavailableReason}`
      : null;
  }

  protected protectiveBeltWarning(): string | null {
    const speciesId = this.formControl.value;
    if (!this.lacksMandatoryProtectiveBelt(speciesId)) {
      return null;
    }
    const species = this.animalSpeciesService
      .allSpecies()
      .find((option) => option.id === speciesId);
    return `${species?.english} must have a protective belt! ${this.props.protectiveBeltUnavailableReason}`;
  }

  private lacksMandatoryProtectiveBelt(speciesId: string | undefined): boolean {
    return (
      !!this.props.protectiveBeltUnavailableReason &&
      this.animalSpeciesService.isMandatoryProtectiveBeltSpecies(speciesId)
    );
  }

  protected displayFn(): (id: string) => string {
    return (id: string) => {
      if (id === 'Other') return id;
      const animalSpecies = this.animalSpeciesService.allSpecies();
      const correspondingOption = Array.isArray(animalSpecies)
        ? animalSpecies.find((option) => option.id === id)
        : null;
      return correspondingOption?.english;
    };
  }

  reload() {
    this.animalSpeciesService.reload$.next();
  }
}
